import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import {
  EMAIL_FOOTER_TOKEN,
  EmailContentData,
  EmailPreviewHydrationData,
  PreviewRenderingService,
} from './../../../../email-builder';
import { EmailEditorPreviewModule } from './../../../../email-editor-preview';
import { DefaultCardAction, Template } from './../card/card.component';
import { Footer } from '../../../../email-builder/src/components/email-advanced-settings/email-advanced-settings.component';

interface PreviewData {
  primaryAction: DefaultCardAction;
  template: Template;
  userId: string;
  ownerId: string;
  partnerId: string;
}

const actionTranslationMap = {
  duplicate: 'COMMON.ACTION_LABELS.DUPLICATE',
  edit: 'COMMON.ACTION_LABELS.EDIT',
  use: 'COMMON.ACTION_LABELS.ADD_EMAIL',
};

@Component({
  selector: 'email-preview-dialog.component',
  imports: [TranslateModule, MatDialogModule, MatButtonModule, EmailEditorPreviewModule, CommonModule],
  providers: [],
  templateUrl: 'preview-dialog.component.html',
  styleUrls: ['preview-dialog.component.scss'],
})
export class PreviewDialogComponent implements OnInit, OnDestroy {
  primaryActionText: string;
  isRendering = false;
  renderedHtml$: Observable<string>;
  hydrationData: EmailPreviewHydrationData;
  subscriptions: Subscription[] = [];
  constructor(
    @Inject(MAT_DIALOG_DATA) public previewData: PreviewData,
    @Inject(EMAIL_FOOTER_TOKEN) @Optional() private readonly footer$: Observable<Footer>,
    public dialogRef: MatDialogRef<PreviewDialogComponent>,
    private translate: TranslateService,
    private emailPreviewService: PreviewRenderingService,
  ) {
    this.hydrationData = {
      business: {
        accountGroupId: this.previewData.ownerId,
        name: '',
        hasSnapshot: false,
        hasSalesperson: false,
      },
      userID: this.previewData.userId,
      partnerID: this.previewData.partnerId,
      marketID: undefined,
      contactID: 'Contact',
    };
    this.primaryActionText = this.translate.instant(actionTranslationMap[this.previewData.primaryAction]);
  }

  ngOnInit(): void {
    this.emailPreviewService.setPreviewHydrationParams(this.hydrationData);
    this.renderEmailContent();
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  closeWithAction(): void {
    this.dialogRef.close(true);
  }

  renderEmailContent(): void {
    this.isRendering = true;
    const footer$ = this.footer$ ? this.footer$ : of(null);
    const templateJson$ = footer$.pipe(
      map((footer) => {
        const templateJson = JSON.parse(this.previewData.template.templateJson) as EmailContentData;
        if (footer) {
          const disclaimer = templateJson?.emailSetup?.footer?.disclaimer ?? '';
          templateJson.emailSetup.footer = footer;
          templateJson.emailSetup.footer.disclaimer = disclaimer;
        }
        return templateJson;
      }),
    );
    const initialRender$ = templateJson$.pipe(
      switchMap((templateJson) => {
        return this.emailPreviewService.renderContentHTML(templateJson);
      }),
    );
    this.renderedHtml$ = combineLatest([templateJson$, initialRender$]).pipe(
      switchMap(([_, renderedHTML]) => {
        return this.emailPreviewService.hydrateTemplate(renderedHTML);
      }),
      finalize(() => {
        this.isRendering = false;
      }),
    );
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
